import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { TimelineArticle } from '../../types/timeline';
import { SupportedLanguage } from '../../types/language';
import { useMultilingual } from '../../hooks/useMultilingual';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { ArrowTopRightOnSquareIcon, UserGroupIcon, MapPinIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

interface ArticleCardProps {
  article: TimelineArticle;
  isMilitaryMode: boolean;
  language: SupportedLanguage;
  onArticleSelect: (article: TimelineArticle) => void;
}

export const ArticleCard: React.FC<ArticleCardProps> = ({
  article,
  isMilitaryMode,
  language,
  onArticleSelect,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const militaryScore = article.categoryScores?.MILITARY || 0;
  const { getMultilingualArray, getMultilingualContent } = useMultilingual(language);
  const { t } = useTranslation();
  
  // Get AI analysis data
  const aiSummary = getMultilingualContent(article.summary);
  const keyPoints = getMultilingualArray(article.keyPoints);
  
  // Parse key entities if available (assuming they're stored as JSON)
  const keyEntities = article.keyEntities || [];
  
  // Get confidence level (assuming it's available in the article data)
  const confidenceLevel = article.confidenceLevel || 0;

  return (
    <div
      className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${
        isMilitaryMode ? 'border-red-200 bg-red-50/30' : ''
      }`}
      onClick={() => {
        onArticleSelect(article);
        try {
          const next = new URLSearchParams(searchParams);
          next.set('article', String(article.id));
          setSearchParams(next, { replace: false });
        } catch {}
      }}
    >
      <div className="flex justify-between items-start mb-3">
        <h4 className="font-medium text-sm text-gray-900 line-clamp-2 flex-1">
          {article.title}
        </h4>
        <div className="ml-3 flex-shrink-0">
          <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${
            article.overallRiskScore >= 8 ? 'bg-red-100 text-red-800' :
            article.overallRiskScore >= 6 ? 'bg-orange-100 text-orange-800' :
            article.overallRiskScore >= 4 ? 'bg-yellow-100 text-yellow-800' :
            'bg-green-100 text-green-800'
          }`}>
            {article.overallRiskScore.toFixed(1)}
          </span>
        </div>
      </div>
      
      {/* Military-specific info */}
      {isMilitaryMode && (
        <div className="mb-2 flex items-center gap-2">
          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">
            🎯 {t('public.articles.military')}: {militaryScore.toFixed(1)}
          </span>
          {article.categoryScores && Object.entries(article.categoryScores)
            .filter(([cat, score]) => cat !== 'MILITARY' && score > 0)
            .slice(0, 2)
            .map(([cat, score]) => (
              <span key={cat} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                {cat}: {score.toFixed(1)}
              </span>
            ))
          }
        </div>
      )}
      
      {/* AI Summary */}
      {aiSummary && (
        <div className="mb-3">
          <div className="text-sm text-gray-900">
            {aiSummary}
          </div>
        </div>
      )}

      {/* Key Points */}
      {keyPoints.length > 0 && (
        <div className="mb-3">
          <div className="text-xs font-semibold text-gray-700 mb-1">🔍 {t('public.articles.analysis')}</div>
          <div className="space-y-1">
            {keyPoints.slice(0, 3).map((point, index) => (
              <div key={index} className="text-xs text-gray-600 flex items-start gap-1">
                <span className="text-gray-400 mt-0.5">•</span>
                <span className="line-clamp-2">{point}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Entities */}
      {keyEntities.length > 0 && (
        <div className="mb-3">
          <div className="text-xs font-semibold text-gray-700 mb-1">🏷️ {t('public.articles.keyEntities')}</div>
          <div className="flex flex-wrap gap-1">
            {keyEntities.slice(0, 6).map((entity, index) => (
              <span key={index} className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                {entity.includes('Russia') || entity.includes('Putin') ? (
                  <UserGroupIcon className="w-3 h-3" />
                ) : entity.includes('Estonia') || entity.includes('Tallinn') ? (
                  <MapPinIcon className="w-3 h-3" />
                ) : (
                  <BuildingOfficeIcon className="w-3 h-3" />
                )}
                {entity}
              </span>
            ))}
          </div>
        </div>
      )}
      
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span className="font-medium">{article.source}</span>
        <span>{formatDistanceToNow(new Date(article.publishedAt), { addSuffix: true })}</span>
      </div>
      
      {article.url && (
        <div className="mt-2">
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
          >
            {t('public.articles.readFullArticle')}
            <ArrowTopRightOnSquareIcon className="w-3 h-3" />
          </a>
        </div>
      )}
    </div>
  );
};
